import React, { FC, useContext } from 'react';
enum Localizations {
  ENG = 'GB',
}

interface AppSettings {
  generalThemeColorMode: 'light' | 'dark';
  generalVaultMaxFeePercentage: number;
  langLocalization: Localizations;
  fetchingOverride: 'chain' | 'graph' | false;
  sidebarCollapsed: boolean;
}

type SettingKeys = keyof AppSettings;

export const DefaultSettings: AppSettings = {
  generalThemeColorMode: 'dark',
  generalVaultMaxFeePercentage: 5,
  langLocalization: Localizations.ENG,
  fetchingOverride: false,
  sidebarCollapsed: false,
};

interface AppSettingsContext {
  settings: AppSettings;
  resetDefaults: (save?: boolean) => void;
  saveSettings: (settings: Partial<AppSettings>, save?: boolean) => void;
  getSetting: (setting: SettingKeys) => any;
  saveToLocal: (newSettings?: Partial<AppSettings>) => void;
  loadFromLocal: () => void;
}

export const SettingsContext = React.createContext<AppSettingsContext | undefined>(undefined);

export const SettingsProvider: FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = React.useState<AppSettings>(DefaultSettings);
  const settingsRef = React.useRef<AppSettings>(settings);
  const [isInit, setInit] = React.useState<boolean>(false);
  const STORAGE_KEY = '_VESTA_APP_SETTINGS';

  function saveToLocal(newSettings?: Partial<AppSettings>) {
    const cfg = newSettings ? newSettings : settingsRef.current;
    if (window) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
    }
  }

  function getSetting(setting: SettingKeys) {
    return settingsRef.current[setting];
  }

  function mutateSettings(settings: Partial<AppSettings>, save?: boolean) {
    setSettings(oldSettings => {
      const newData = { ...oldSettings, ...settings };
      settingsRef.current = newData;
      if (save) saveToLocal(newData);
      return newData;
    });
  }

  function loadFromLocal() {
    if (!window) return;

    const localSettings = window.localStorage.getItem(STORAGE_KEY);

    if (localSettings) mutateSettings(JSON.parse(localSettings));
    else mutateSettings(DefaultSettings);
  }

  const resetDefaults = (save?: boolean) => {
    setSettings(DefaultSettings);
    settingsRef.current = DefaultSettings;
    save ? saveToLocal(DefaultSettings) : null;
  };

  React.useEffect(() => {
    if (!isInit) {
      setInit(true);
      loadFromLocal();
    }
  });

  return (
    <SettingsContext.Provider
      value={{
        settings: settingsRef.current ?? DefaultSettings,
        saveToLocal,
        saveSettings: mutateSettings,
        getSetting,
        loadFromLocal,
        resetDefaults,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useAppSettings = () => {
  const ctx = useContext(SettingsContext);

  if (!ctx) throw 'useAppSettings usage not wrapped in <SettingsContext>';

  return ctx;
};
